import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { EMPTY, Observable, switchMap, take } from "rxjs";
import { environment } from "src/environments/environment";
import { UnitAttributes } from "src/models/units/unit-attributes.model";
import { UnitDataService } from "src/services/unit-data.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { ResolverHelper } from "./resolver.helper";

export const EvMapResolver: ResolveFn<any> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> => {
    const queryParams = route.queryParams;
    const hasKnr = queryParams.hasOwnProperty("knr");
    const hasBnr = queryParams.hasOwnProperty("bnr");
    const hasGnr = queryParams.hasOwnProperty("gnr");
    const hasOrgnumber = queryParams.hasOwnProperty("organizationNumber");
    const hasShareNumber = queryParams.hasOwnProperty("shareNumber");
    const hasLatitude = queryParams.hasOwnProperty("lat");
    const hasLongitude = queryParams.hasOwnProperty("lon");
    const hasAreaId = queryParams.hasOwnProperty("areaId");
    const hasAreaTypeId = queryParams.hasOwnProperty("areaTypeId");
    const hasUnitId = queryParams.hasOwnProperty("unitId") || queryParams.hasOwnProperty("unitID");
    const hasMaxLat = queryParams.hasOwnProperty("maxLat");
    const hasMaxLng = queryParams.hasOwnProperty("maxLng");
    const hasMinLat = queryParams.hasOwnProperty("minLat");
    const hasMinLng = queryParams.hasOwnProperty("minLng");
    const unitDataService = inject(UnitDataService);
    const authService = inject(AuthService);
    const router = inject(Router);

    return ResolverHelper.hasPermission(authService, [EVServiceType.Internal_Debug]).pipe(
        take(1),
        switchMap((hasDebugRole) => {
            console.log("queryParams:", queryParams);
            if (hasUnitId) {
                const unitId: string =
                    queryParams["unitId"] != null ? queryParams["unitId"].toString() : queryParams["unitID"] != null ? queryParams["unitID"].toString() : "";
                return unitDataService.getUnitNavigationInfo(unitId).pipe(
                    switchMap((unitNavigationInfo: any) => {
                        if (unitNavigationInfo) {
                            // router.navigate(["/map/estate/market"], { queryParams: { unitId: unitId } });
                            //TODO: Cleanup after all is good in 5.0
                            if (hasDebugRole) {
                                router.navigate(["/map/estate/market"], { queryParams: { unitId: unitId } });
                            } else {
                                //todo: implement redirect to EvWeb with unitId
                                //Må avgjøre hvordan vi skal redirekte, estateID orgNummer eller annet
                                console.warn("Redirecting to EvWeb with unitId, not implemented:", unitId);
                                return redirectToSearchPage(hasDebugRole);
                            }
                        }
                        return EMPTY;
                    }),
                );
            } else if (hasOrgnumber && hasShareNumber) {
                const organizationNumber: string = queryParams["organizationNumber"] != null ? queryParams["organizationNumber"].toString() : "";
                const shareNumber: string = queryParams["shareNumber"] != null ? queryParams["shareNumber"].toString() : "";

                const searchString = organizationNumber + " " + shareNumber;
                return unitDataService.getUnitAttributes(searchString).pipe(
                    switchMap((units: UnitAttributes[]) => {
                        if (units.length == 1) {
                            // router.navigate(["/map/estate/market"], { queryParams: { unitId: units[0].unitId } });
                            //TODO: Cleanup after all is good in 5.0
                            if (hasDebugRole) {
                                router.navigate(["/map/estate/market"], { queryParams: { unitId: units[0].unitId } });
                            } else {
                                window.open(
                                    environment.evwebUrl +
                                        "/search/market;orgNumber=" +
                                        units[0].organizationNumber +
                                        ";shareNumber=" +
                                        units[0].shareNumber +
                                        ";unitID=" +
                                        units[0].unitId,
                                    "_self",
                                );
                            }
                            return EMPTY;
                        } else {
                            return redirectToSearchPage(hasDebugRole);
                        }
                    }),
                );
            } else if (hasKnr && hasBnr && hasGnr) {
                const KNr: string = queryParams["knr"] != null ? queryParams["knr"].toString() : "";
                const BNr: string = queryParams["bnr"] != null ? queryParams["bnr"].toString() : "";
                const GNr: string = queryParams["gnr"] != null ? queryParams["gnr"].toString() : "";
                const FNr: string = queryParams["fnr"] != null ? queryParams["fnr"].toString() : "";
                const SNr: string = queryParams["snr"] != null ? queryParams["snr"].toString() : "";

                const searchString = KNr + " " + GNr + " " + BNr + " " + FNr + " " + SNr;

                return unitDataService.getUnitAttributes(searchString).pipe(
                    switchMap((units: UnitAttributes[]) => {
                        //Søk om det finnes noen eiendom på matrikkelen
                        if (units.length == 1) {
                            //TODO: Cleanup after all is good in 5.0
                            if (hasDebugRole) {
                                router.navigate(["/map/estate/market"], { queryParams: { unitId: units[0].unitId } });
                            } else {
                                window.open(
                                    environment.evwebUrl +
                                        "/search/market;estateID=" +
                                        units[0].estateID +
                                        ";addressID=" +
                                        units[0].addressID +
                                        ";unitID=" +
                                        units[0].unitId,
                                    "_self",
                                );
                            }
                            return EMPTY;
                        } else {
                            return redirectToSearchPage(hasDebugRole);
                        }
                    }),
                );
            } else if (hasLatitude && hasLongitude) {
                let lat: string = queryParams["lat"] != null ? queryParams["lat"].toString() : "";
                let lon: string = queryParams["lon"] != null ? queryParams["lon"].toString() : "";
                const zoom: string = queryParams["zoom"] != null ? queryParams["zoom"].toString() : "";
                const estateId: string = queryParams["estateId"] != null ? queryParams["estateId"].toString() : "";

                if (lat.includes(",")) {
                    lat = lat.replace(",", ".");
                }
                if (lon.includes(",")) {
                    lon = lon.replace(",", ".");
                }

                const navParams: Record<string, string> = { lat, lon, zoom };
                if (estateId) navParams["estateID"] = estateId;
                // router.navigate(["/map/estate/market"], { queryParams: navParams });
                //TODO: Cleanup after all is good in 5.0
                if (hasDebugRole) {
                    const navParams: Record<string, string> = { lat, lon, zoom };
                    if (estateId) navParams["estateID"] = estateId;
                    router.navigate(["/map/estate/market"], { queryParams: navParams });
                } else {
                    let url = environment.evwebUrl + "/search/market;lat=" + lat + ";lon=" + lon + ";zoom=" + zoom;
                    if (estateId != null && estateId != "") url += ";estateID=" + estateId;
                    window.open(url, "_self");
                }
            } else if (hasAreaId && hasAreaTypeId) {
                const areaId: string = queryParams["areaId"] != null ? queryParams["areaId"].toString() : "";
                const areaTypeId: string = queryParams["areaTypeId"] != null ? queryParams["areaTypeId"].toString() : "";

                // router.navigate(["/map/estate/market"], { queryParams: { areaId, areaTypeId } });
                //TODO: Cleanup after all is good in 5.0
                if (hasDebugRole) {
                    router.navigate(["/map/estate/market"], { queryParams: { areaId, areaTypeId } });
                } else {
                    window.open(environment.evwebUrl + "/search/market;areaId=" + areaId + ";areaTypeId=" + areaTypeId, "_self");
                }
            } else if (hasMaxLat && hasMaxLng && hasMinLat && hasMinLng) {
                const maxLat: string = queryParams["maxLat"] != null ? queryParams["maxLat"].toString() : "";
                const maxLng: string = queryParams["maxLng"] != null ? queryParams["maxLng"].toString() : "";
                const minLat: string = queryParams["minLat"] != null ? queryParams["minLat"].toString() : "";
                const minLng: string = queryParams["minLng"] != null ? queryParams["minLng"].toString() : "";

                if (hasDebugRole) {
                    router.navigate(["/map/estate/market"], { queryParams: { maxLat, maxLng, minLat, minLng } });
                } else {
                    window.open(
                        environment.evwebUrl + "/search/market;maxLat=" + maxLat + ";maxLng=" + maxLng + ";minLat=" + minLat + ";minLng=" + minLng,
                        "_self",
                    );
                }
            } else {
                console.log("No valid query parameters provided for EvMapResolver");
                return ResolverHelper.redirectToFrontPage(authService, router);
            }

            return EMPTY;
        }),
    );

    function redirectToSearchPage(hasDebugRole: boolean) {
        let queryString = "";
        if (hasKnr && hasBnr && hasGnr) {
            const KNr: string = queryParams["knr"] != null ? queryParams["knr"].toString() : "";
            const BNr: string = queryParams["bnr"] != null ? queryParams["bnr"].toString() : "";
            const GNr: string = queryParams["gnr"] != null ? queryParams["gnr"].toString() : "";
            const FNr: string = queryParams["fnr"] != null ? queryParams["fnr"].toString() : "";
            const SNr: string = queryParams["snr"] != null ? queryParams["snr"].toString() : "";

            queryString = [KNr, GNr, BNr, FNr, SNr].join(" ").trim();
        } else if (hasOrgnumber) {
            const orgNr: string = queryParams["organizationNumber"] != null ? queryParams["organizationNumber"].toString() : "";
            const shareNumber: string = queryParams["shareNumber"] != undefined ? queryParams["shareNumber"].toString() : "";

            queryString = [orgNr, shareNumber].join(" ").trim();
        } else {
            return ResolverHelper.redirectToFrontPage(authService, router);
        }

        if (hasDebugRole) {
            // The router encodes query params itself, and SearchpageComponent reads "searchstring"
            router.navigate(["/searchpage"], { queryParams: { searchstring: queryString } });
        } else {
            const url = environment.evwebUrl + "/searchpage;searchString=" + encodeURIComponent(queryString) + ";doSearch=true;";
            window.open(url, "_self");
        }

        return EMPTY;
    }
};
